import React, { useState, useRef, useEffect } from "react";
import { safeText } from "../utils/hubHelpers";

export default function SiteSearchSelector({
  sites = [],
  selectedSiteId = "",
  onSelectSite,
  countsMap = {}, // { siteId: count }
  totalCount = 0,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const safeSites = Array.isArray(sites) ? sites : [];

  const filteredSites = safeSites.filter((s) => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    const name = (s.name || s.label || "").toLowerCase();
    const code = (s.siteCode || s.code || "").toLowerCase();
    return name.includes(q) || code.includes(q);
  });

  const selectedSite = safeSites.find((s) => s.id === selectedSiteId);

  // Top active sites (with highest counts) for quick shortcut chips
  const topSites = [...safeSites]
    .filter((s) => countsMap[s.id] > 0)
    .sort((a, b) => (countsMap[b.id] || 0) - (countsMap[a.id] || 0))
    .slice(0, 4);

  return (
    <div className="site-search-selector-container mb-2" ref={dropdownRef}>
      <div className="d-flex align-items-center gap-2 flex-wrap">
        {/* Searchable Dropdown Button */}
        <div className="position-relative" style={{ minWidth: 240, flex: "0 1 290px" }}>
          <div
            className={`hub-site-trigger ${selectedSiteId ? "is-active" : ""}`}
            onClick={() => setIsOpen(!isOpen)}
          >
            <div className="d-flex align-items-center gap-2 text-truncate" style={{ minWidth: 0 }}>
              <span style={{ fontSize: "13px" }}>📍</span>
              <span className="text-truncate" style={{ fontSize: "11.5px" }}>
                {selectedSite ? (
                  <>
                    <span style={{ color: "#64748b", fontWeight: 500, marginRight: 4, fontSize: "11px" }}>Site:</span>
                    <strong style={{ color: "#0f172a", fontWeight: 700, fontSize: "11.5px" }}>{safeText(selectedSite.name, "Site")}</strong>
                  </>
                ) : (
                  <span style={{ color: "#334155", fontWeight: 600, fontSize: "11.5px" }}>All Project & O&M Sites</span>
                )}
              </span>
            </div>

            <div className="d-flex align-items-center gap-1">
              {selectedSiteId && (
                <span
                  className="badge bg-primary text-white"
                  style={{ fontSize: "10.5px", borderRadius: 4, padding: "2px 5px" }}
                >
                  {countsMap[selectedSiteId] ?? 0}
                </span>
              )}
              <span style={{ color: "#94a3b8", fontSize: "10.5px" }}>{isOpen ? "▲" : "▼"}</span>
            </div>
          </div>

          {/* Dropdown Menu */}
          {isOpen && (
            <div
              className="site-selector-dropdown"
              style={{
                position: "absolute",
                top: "calc(100% + 4px)",
                left: 0,
                right: 0,
                background: "#ffffff",
                border: "1px solid #cbd5e1",
                borderRadius: 8,
                boxShadow: "0 10px 25px -3px rgba(0, 0, 0, 0.12), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                zIndex: 1000,
                maxHeight: 320,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              {/* Search Bar inside dropdown */}
              <div style={{ padding: "0.45rem", borderBottom: "1px solid #f1f5f9", background: "#f8fafc" }}>
                <div style={{ position: "relative" }}>
                  <span style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: "12px" }}>
                    🔍
                  </span>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Type to search 90+ sites..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoFocus
                    style={{
                      paddingLeft: "1.75rem",
                      fontSize: "12px",
                      borderRadius: 6,
                      border: "1px solid #cbd5e1",
                      height: "30px",
                    }}
                  />
                </div>
              </div>

              {/* Options List */}
              <div style={{ overflowY: "auto", flex: 1, padding: "0.2rem 0" }}>
                {/* All Sites Option */}
                <div
                  className="site-option-item"
                  onClick={() => {
                    onSelectSite("");
                    setIsOpen(false);
                    setSearchTerm("");
                  }}
                  style={{
                    padding: "0.45rem 0.75rem",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background: selectedSiteId === "" ? "#f1f5f9" : "transparent",
                    fontWeight: selectedSiteId === "" ? 700 : 500,
                    color: "#0f172a",
                    fontSize: "12px",
                    borderBottom: "1px dashed #f1f5f9",
                  }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <span>🌐</span>
                    <span>All Sites Combined</span>
                  </div>
                  <span className="badge bg-secondary text-white" style={{ fontSize: "11px" }}>
                    {totalCount}
                  </span>
                </div>

                {filteredSites.length === 0 ? (
                  <div className="text-center py-3 text-muted small" style={{ fontSize: "11.5px" }}>No sites match "{searchTerm}"</div>
                ) : (
                  filteredSites.map((s) => {
                    const isSelected = s.id === selectedSiteId;
                    const count = countsMap[s.id] || 0;
                    return (
                      <div
                        key={s.id}
                        className="site-option-item"
                        onClick={() => {
                          onSelectSite(s.id);
                          setIsOpen(false);
                          setSearchTerm("");
                        }}
                        style={{
                          padding: "0.45rem 0.75rem",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          background: isSelected ? "#eff6ff" : "transparent",
                          color: isSelected ? "#2563eb" : "#334155",
                          fontWeight: isSelected ? 700 : 500,
                          fontSize: "12px",
                          transition: "background 0.1s",
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) e.currentTarget.style.background = "#f8fafc";
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) e.currentTarget.style.background = "transparent";
                        }}
                      >
                        <div className="d-flex align-items-center gap-2 text-truncate" style={{ maxWidth: "80%" }}>
                          <span style={{ fontSize: "12px", color: isSelected ? "#2563eb" : "#64748b" }}>📍</span>
                          <span className="text-truncate">{safeText(s.name, "Site")}</span>
                        </div>
                        <span
                          className={`badge ${count > 0 ? (isSelected ? "bg-primary" : "bg-light text-dark border") : "bg-light text-muted"}`}
                          style={{ fontSize: "10.5px", borderRadius: 4 }}
                        >
                          {count}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* Quick Shortcut Chips: All Sites + Active Sites */}
        <div className="d-flex align-items-center gap-2 flex-wrap" style={{ flex: 1 }}>
          <button
            type="button"
            className={`hub-site-chip ${selectedSiteId === "" ? "active" : ""}`}
            onClick={() => onSelectSite("")}
          >
            <span>All Sites ({totalCount})</span>
          </button>

          {topSites.map((s) => {
            const isSelected = selectedSiteId === s.id;
            return (
              <button
                key={s.id}
                type="button"
                className={`hub-site-chip ${isSelected ? "active" : ""}`}
                onClick={() => onSelectSite(isSelected ? "" : s.id)}
              >
                <span>📍 {safeText(s.name, "Site")}</span>
                <span className="hub-site-chip-badge">
                  {countsMap[s.id] || 0}
                </span>
              </button>
            );
          })}

          {selectedSiteId && (
            <button
              type="button"
              className="btn btn-sm btn-link text-danger text-decoration-none p-0 ms-1 fw-bold"
              onClick={() => onSelectSite("")}
              style={{ fontSize: "11px" }}
            >
              ✕ Clear Filter
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
